import { Stethoscope, TrendingDown, Check, HelpCircle, Target } from "lucide-react";
import logo from "@/assets/logo.png";

const SNAPSHOT = [
  { label: "Night sweats", value: "18 of 30 nights", pct: 60 },
  { label: "Sleep", value: "~5.2 hrs · waking 2x a night", pct: 45 },
  { label: "Hot flushes", value: "~6 a day · worse mid-afternoon", pct: 65 },
  { label: "Mood", value: "Low or irritable on 12 days", pct: 40 },
  { label: "Energy", value: "Averaging 4 out of 10", pct: 40 },
];

const TRIED = [
  "Cut caffeine after midday — fewer afternoon flushes",
  "10 min wind down + cooler bedroom — fell asleep faster on 15 nights",
  "Protein at breakfast — steadier energy, fewer late-morning crashes",
  "Daily 20 min walk + 2 strength sessions — mood lift, less stiffness",
];

const QUESTIONS = [
  "Are my symptoms consistent with perimenopause, or should other causes be ruled out?",
  "Is HRT an option given my history, and what are the benefits and risks for me?",
  "What will help most with night sweats and broken sleep?",
  "Should I check my blood pressure, cholesterol or bone health?",
  "What are my non-hormonal options if HRT isn't suitable?",
];

const PRIORITIES = ["Sleep through the night", "Steady mood and focus at work", "Confidence in my long-term health"];

export const SampleReport = () => {
  return (
    <div className="relative bg-white rounded-[1.75rem] card-shadow-hover overflow-hidden" data-testid="sample-report">
      {/* sample ribbon */}
      <div className="absolute top-0 right-0 w-28 h-28 overflow-hidden pointer-events-none">
        <div className="absolute top-5 -right-9 rotate-45 w-40 text-center bg-[var(--amber)] text-brand text-[11px] font-bold tracking-widest py-1.5 shadow-sm">
          SAMPLE
        </div>
      </div>

      {/* document header */}
      <div className="px-6 sm:px-9 pt-8 pb-6 border-b border-subtle">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Meno Flames and Finesse" className="h-12 w-auto" />
          <div className="h-9 w-px bg-[var(--border-subtle,#e7e2da)]" />
          <div>
            <div className="flex items-center gap-1.5 text-brand">
              <Stethoscope size={15} />
              <span className="font-serif text-lg text-ink leading-none">Doctor's Appointment Report</span>
            </div>
            <div className="text-ink-muted text-xs mt-1">Prepared for Jane D., 51 · Based on 30 days of notes</div>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-9 py-7 space-y-7">
        {/* symptom snapshot */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={15} className="text-brand" />
            <h4 className="font-semibold text-ink tracking-wide uppercase text-[0.7rem]">Symptom snapshot</h4>
          </div>
          <div className="space-y-3">
            {SNAPSHOT.map((s, i) => (
              <div key={i}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-ink text-sm font-medium">{s.label}</span>
                  <span className="text-ink-muted text-xs text-right">{s.value}</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-app overflow-hidden">
                  <div className="h-full rounded-full bg-brand" style={{ width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* what I've tried */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Check size={15} className="text-brand" />
            <h4 className="font-semibold text-ink tracking-wide uppercase text-[0.7rem]">What I've tried, and how it helped</h4>
          </div>
          <ul className="space-y-2">
            {TRIED.map((t, i) => (
              <li key={i} className="flex items-start gap-2.5 bg-app rounded-xl px-3.5 py-2.5">
                <div className="w-4 h-4 rounded-full bg-sage flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={11} className="text-brand" strokeWidth={3} />
                </div>
                <span className="text-ink-soft text-sm leading-snug">{t}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* questions */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle size={15} className="text-brand" />
            <h4 className="font-semibold text-ink tracking-wide uppercase text-[0.7rem]">Questions for my doctor</h4>
          </div>
          <ol className="space-y-2.5">
            {QUESTIONS.map((q, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-ink-soft text-sm leading-snug">{q}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* priorities */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Target size={15} className="text-brand" />
            <h4 className="font-semibold text-ink tracking-wide uppercase text-[0.7rem]">What matters most to me</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRIORITIES.map((p) => (
              <span key={p} className="bg-sage text-brand text-xs font-medium px-3.5 py-1.5 rounded-full">
                {p}
              </span>
            ))}
          </div>
        </section>
      </div>

      {/* footer disclaimer */}
      <div className="px-6 sm:px-9 py-4 bg-app-secondary border-t border-subtle">
        <p className="text-ink-muted text-[11px] leading-relaxed">
          This is a sample summary of one person's own self-reported notes over 30 days. It is not a medical
          assessment or diagnosis. Your report is built from your own entries and is yours to keep, print and share.
        </p>
      </div>
    </div>
  );
};
